import type { RoofEdge } from "@logic/lib/design"
import type { Edge2d, Edge2dComputed } from "@logic/lib/mesh"
import { scaleInchesToImage } from "@logic/lib/utils"
import { string } from "fp-ts"
import type { GetRecoilValue, RecoilState } from "recoil"
import { atomFamily, atom, selectorFamily } from "recoil"

import { designLocalState } from "~/containers/Proposal/state/designs/design-state"
import { siteLocalState } from "~/containers/Proposal/state/site/site-state"
import { selectObject } from "~/utils/recoil"

import { findNodeState, findProjectedNodeState, projectNode } from "./nodes"
import type { TransactionOperation } from "./transactions"
import { readonlyTransactors } from "./transactions"
import { getPreferredImage } from "./transactions/getPreferredImage"
import type { SvgStrokeProps } from "../../Studio/Elements/STROKES"
import { EDGE_APPEARANCES } from "../../Studio/Elements/STROKES"
import { lineWeightState } from "../view"

export const DEFAULT_EDGE: RoofEdge = {
  node1id: ``,
  node2id: ``,
  edgeType: `rake`,
}

export const edgeIdIsBad = (edgeId: string): boolean => {
  const [idA, idB] = edgeId.split(`/`)

  return idA === idB
}
export const edgeIdIsGood = (edgeId: string): boolean => !edgeIdIsBad(edgeId)

export const edgeIndex = atom<string[]>({
  key: `edgeIndex`,
  default: [],
  effects: [
    ({ setSelf, onSet }) => {
      onSet((newValue) => setSelf(newValue.filter(edgeIdIsGood)))
    },
  ],
})
export const findEdgeState = atomFamily<RoofEdge, string>({
  key: `edge`,
  default: DEFAULT_EDGE,
})
export const allEdgesState = selectObject(
  `edge_design`,
  findEdgeState,
  edgeIndex,
)

export const edgeSelectionState = atom<Set<string>>({
  key: `edgeSelection`,
  default: new Set(),
})

export const extractEdge = <EDGE extends Edge2d>(
  get: GetRecoilValue,
  edgeId: string,
  findState: (edgeId: string) => RecoilState<EDGE>,
): EDGE & Edge2dComputed => {
  const edge = get(findState(edgeId))

  return {
    ...edge,
    p1: get(findNodeState(edge.node1id)),
    p2: get(findNodeState(edge.node2id)),
  }
}

export const computeEdge = <EDGE extends Edge2d>(
  get: GetRecoilValue,
  edgeId: string,
  findState: (edgeId: string) => RecoilState<EDGE>,
): EDGE & Edge2dComputed => {
  const edge = get(findState(edgeId))

  return {
    ...edge,
    p1: get(findNodeState(edge.node1id)),
    p2: get(findNodeState(edge.node2id)),
  }
}

export const getComputedEdges: TransactionOperation<
  string,
  Edge2dComputed & RoofEdge & { props: SvgStrokeProps; setback: number }
> = (transactors, edgeId) => {
  const { get } = transactors
  const edge = computeEdge(get, edgeId, findEdgeState)
  const lineWeight = get(lineWeightState)
  const { latitude } = get(siteLocalState)
  const setbackInches = get(designLocalState).setbacks[edge.edgeType]
  const { zoomLevel } = getPreferredImage(readonlyTransactors(get))
  const setback = scaleInchesToImage({
    inches: setbackInches,
    latitude,
    zoomLevel,
  })

  return {
    ...edge,
    setback,
    props: {
      stroke: EDGE_APPEARANCES[edge.edgeType].color,
      ...EDGE_APPEARANCES[edge.edgeType].trace(lineWeight),
    },
  }
}

export const findComputedEdgeState = selectorFamily<
  Edge2dComputed & RoofEdge & { props: SvgStrokeProps; setback: number },
  string
>({
  key: `edgeComputed`,
  get:
    (edgeId) =>
    ({ get }) =>
      getComputedEdges(readonlyTransactors(get), edgeId),
  set:
    (edgeId) =>
    ({ get, set }, segment) => {
      const { p1, p2 } = segment as Edge2dComputed
      const { node1id, node2id } = get(findEdgeState(edgeId))
      set(findNodeState(node1id), p1)
      set(findNodeState(node2id), p2)
    },
})

export const projectEdge = <EDGE extends Edge2d>(
  get: GetRecoilValue,
  edgeId: string,
  findState: (edgeId: string) => RecoilState<EDGE>,
): EDGE & Edge2dComputed => {
  const edge = get(findState(edgeId))
  const { node1id, node2id } = edge
  const p1 = projectNode(get, node1id)
  const p2 = projectNode(get, node2id)

  return {
    ...edge,
    p1,
    p2,
  }
}

export const findProjectedEdgeState = selectorFamily<
  Edge2dComputed & RoofEdge & { props: SvgStrokeProps; setback: number },
  string
>({
  key: `edgeProjected`,
  get:
    (edgeId) =>
    ({ get }) => {
      const edge = projectEdge(get, edgeId, findEdgeState)
      const lineWeight = get(lineWeightState)
      const { latitude } = get(siteLocalState)
      const setbackInches = get(designLocalState).setbacks[edge.edgeType]
      const { zoomLevel } = getPreferredImage(readonlyTransactors(get))
      const setback = scaleInchesToImage({
        inches: setbackInches,
        latitude,
        zoomLevel,
      })

      return {
        ...edge,
        setback,
        props: {
          stroke: EDGE_APPEARANCES[edge.edgeType].color,
          ...EDGE_APPEARANCES[edge.edgeType].trace(lineWeight),
        },
      }
    },
  set:
    (edgeId) =>
    ({ get, set }, segment) => {
      const { p1, p2 } = segment as Edge2dComputed
      const { node1id, node2id } = get(findEdgeState(edgeId))
      set(findProjectedNodeState(node1id), p1)
      set(findProjectedNodeState(node2id), p2)
    },
})
